// src/components/TripCard.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { updateTrip, deleteTrip } from '../services/tripService';
import { showConfirm, showDeleteConfirm, showSuccess, showError } from '../services/alertService';
import './TripCard.css';

const TripCard = ({ trip, onUpdate }) => {
  const [loading, setLoading] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-TW');
  };

  const getTotalCost = (totalCost) => {
    if (!totalCost) return 0;
    
    // 如果 totalCost 是物件，計算所有分類的總和
    if (typeof totalCost === 'object') {
      return Object.values(totalCost).reduce((sum, cost) => sum + (Number(cost) || 0), 0);
    }
    
    // 如果是數字，直接返回
    return Number(totalCost) || 0;
  };

  const isExpired = () => {
    if (!trip.end_date) return false;
    return new Date(trip.end_date) < new Date();
  };

  const getDaysUntilEnd = () => {
    if (!trip.end_date) return null;
    const today = new Date();
    const endDate = new Date(trip.end_date);
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleCompleteTrip = async () => {
    const result = await showConfirm(
      '完成行程',
      '確定要標記這個行程為完成嗎？完成後就無法再編輯行程內容。',
      {
        confirmButtonText: '標記完成',
        confirmButtonColor: '#10b981'
      }
    );

    if (result) {
      setLoading(true);
      try {
        await updateTrip(trip.id, { isCompleted: true });
        await showSuccess(
          '行程完成！',
          '你的行程已成功標記為完成，可以在已完成的行程中查看。'
        );
        onUpdate();
      } catch (error) {
        console.error('Error completing trip:', error);
        await showError(
          '標記失敗',
          '標記完成時發生錯誤，請稍後再試。'
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handleTogglePublic = async () => {
    const action = trip.isPublic ? '設為私人' : '發佈為公開';
    const title = trip.isPublic ? '設為私人行程' : '發佈公開行程';
    const text = trip.isPublic 
      ? '將行程設為私人後，其他用戶將無法查看此行程。'
      : '發佈為公開後，其他用戶可以查看你的行程並給予評分。';

    const result = await showConfirm(
      title,
      text,
      {
        confirmButtonText: action,
        confirmButtonColor: trip.isPublic ? '#6b7280' : '#8b5cf6'
      }
    );

    if (result) {
      setLoading(true);
      try {
        await updateTrip(trip.id, { isPublic: !trip.isPublic });
        await showSuccess(
          '更新成功！',
          `行程已${action}。`
        );
        onUpdate();
      } catch (error) {
        console.error('Error toggling public status:', error);
        await showError(
          '更新失敗',
          '更新行程狀態時發生錯誤，請稍後再試。'
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteTrip = async () => {
    const result = await showDeleteConfirm(
      '刪除行程',
      `確定要刪除「${trip.title}」嗎？\n\n此操作將永久刪除行程及其所有景點資料，且無法復原。`
    );

    if (result) {
      setLoading(true);
      try {
        await deleteTrip(trip.id);
        await showSuccess(
          '刪除成功！',
          '行程已成功刪除。'
        );
        onUpdate();
      } catch (error) {
        console.error('Error deleting trip:', error);
        await showError(
          '刪除失敗',
          '刪除行程時發生錯誤，請稍後再試。'
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const daysUntilEnd = getDaysUntilEnd();
  const expired = isExpired();

  return (
    <div className={`trip-card ${trip.isCompleted ? 'completed' : ''} ${expired && !trip.isCompleted ? 'expired' : ''}`}>
      {/* 行程狀態標籤和刪除按鈕 */}
      <div className="trip-status-bar">
        <div className="status-badges">
          {trip.isCompleted ? (
            <span className="status-badge completed">✅ 已完成</span>
          ) : expired ? (
            <span className="status-badge expired">⏰ 已結束</span>
          ) : daysUntilEnd !== null && daysUntilEnd <= 7 && daysUntilEnd >= 0 ? (
            <span className="status-badge soon">🔥 即將結束 ({daysUntilEnd} 天)</span>
          ) : (
            <span className="status-badge ongoing">🚀 進行中</span>
          )}
          
          {trip.isPublic && <span className="status-badge public">🌐 公開</span>}
        </div>
        
        <button 
          className="delete-trip-btn"
          onClick={handleDeleteTrip}
          disabled={loading}
          title="刪除行程"
        >
          🗑️
        </button>
      </div>

      <div className="trip-card-header">
        <h3 className="trip-title">{trip.title}</h3>
      </div>

      <div className="trip-info">
        <div className="trip-location">
          <span className="country">📍 {trip.country}</span>
          <span className="city">{trip.city}</span>
        </div>
        
        <div className="trip-dates">
          <span className="date-label">📅 開始:</span>
          <span className="date">{formatDate(trip.start_date)}</span>
          <span className="date-label">🏁 結束:</span>
          <span className="date">{formatDate(trip.end_date)}</span>
        </div>

        <div className="trip-stats">
          <div className="stat">
            <span className="stat-label">💰 總花費:</span>
            <span className="stat-value">
              NT$ {(trip.actualTotalCost || getTotalCost(trip.totalCost)).toLocaleString()}
            </span>
          </div>
          
          {/* 顯示創建者評分 */}
          {trip.creatorAverageRating && (
            <div className="stat">
              <span className="stat-label">⭐ 創建者評分:</span>
              <span className="stat-value creator-rating">
                {trip.creatorAverageRating} 星
              </span>
            </div>
          )}
        </div>
      </div>

      {/* 大型完成按鈕（針對已結束但未完成的行程） */}
      {!trip.isCompleted && expired && (
        <div className="trip-completion-prompt">
          <p className="completion-message">
            🎉 行程已結束！記得標記為完成來保存你的回憶
          </p>
          <button 
            onClick={handleCompleteTrip}
            disabled={loading}
            className="large-complete-btn"
          >
            {loading ? '處理中...' : '🏆 標記行程完成'}
          </button>
        </div>
      )}

      {/* 即將結束提醒 */}
      {!trip.isCompleted && !expired && daysUntilEnd !== null && daysUntilEnd <= 3 && daysUntilEnd >= 0 && (
        <div className="trip-ending-soon">
          <p className="ending-message">
            ⚠️ 行程即將在 {daysUntilEnd} 天後結束
          </p>
          <button 
            onClick={handleCompleteTrip}
            disabled={loading}
            className="early-complete-btn"
          >
            提前完成
          </button>
        </div>
      )}

      <div className="trip-card-footer">
        <Link 
          to={`/trip/${trip.id}`} 
          className="view-trip-btn"
        >
          📋 查看詳情
        </Link>
        
        {/* 快速操作按鈕 */}
        <div className="quick-actions">
          {!trip.isCompleted && (
            <button 
              onClick={handleCompleteTrip}
              disabled={loading}
              className="quick-complete-btn"
              title="標記完成"
            >
              ✅
            </button>
          )}
          
          <button 
            onClick={handleTogglePublic}
            disabled={loading}
            className={`quick-public-btn ${trip.isPublic ? 'public' : 'private'}`}
            title={trip.isPublic ? '設為私人' : '設為公開'}
          >
            {trip.isPublic ? '🔒' : '🌐'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TripCard;